import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Flex,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr
} from '@chakra-ui/react';
import {
  Button,
  Switch,
  SearchInput,
  ButtonText,
  useToast,
  ClearButton,
  Badge
} from '@morressier/alembic';
import TablePagination, {
  DEFAULT_PAGE_SIZE
} from 'src/components/TablePagination';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  EventAndEntityNameMap,
  reminderPhaseLabels
} from '@publishing-engine/domain/actions/v1/events/email-events';
import SubmissionLoader from 'src/pages/Organization/Workspace/Submissions/SubmissionLoader';
import {
  faDeleteRight,
  faDownload,
  faEnvelope,
  faSearch,
  faXmark
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import EditTemplateModal from './EditTemplateModal';
import { useForm } from 'react-hook-form';
import {
  editEmailTemplateById,
  EventItem,
  exportEmailTemplates,
  getAllTemplates,
  getEmailEventsWithSubEvents,
  IListTemplatesQuery,
  IValidateDefaultTemplate,
  validateDefaultTemplate
} from 'src/api/email-service';
import StatusActionModal from './StatusActionModal';
import RichTextPreview from 'src/components/Submission/GenericFieldComponents/RichText/RichTextPreview';
import TemplatePreview from './TemplatePreview';
import { EmailTemplateDoc } from '@publishing-engine/domain/models/email-templates';
import dateFormat from 'dateformat';
import FilterDrawer from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/Filters/FilterDrawer';
import { fetchSubmissionsDashBoardConfig } from 'src/api/queries';
import { useWorkspace } from '../../SubmissionContexts/Users/userHook';
import { parseEmailQueryParams } from 'src/utils/submissionsList';
import EmailActionMenu from './EmailActionMenu';
import DefaultEmailTemplateModal from './DefaultEmailTemplateModal';
import DeleteTemplateModal from './DeleteTemplateModal';
import { useSearchParams } from 'react-router-dom';

// Define table column structure
interface TableColumn {
  key: string;
  label: string;
  size: string | number;
}

type filtersItem =
  | 'category'
  | 'scope'
  | 'active'
  | 'event'
  | 'submissionContextId';

const TEMPLATE_TABLE_COLUMNS: TableColumn[] = [
  {
    key: 'id',
    label: 'ID',
    size: 3
  },
  {
    key: 'name',
    label: 'Title',
    size: 3
  },
  {
    key: 'event',
    label: 'Event',
    size: 2
  },
  {
    key: 'category',
    label: 'Email Tag',
    size: 2
  },
  {
    key: 'scope',
    label: 'Scope',
    size: 2
  }
];

const defaultValues: IListTemplatesQuery = {
  event: [],
  submissionContextId: [],
  category: [],
  active: [],
  scope: [],
  phases: [],
  search: ''
};

const getExportFileName = (contentDisposition: string | undefined) => {
  const fileNameMatch = contentDisposition?.match(
    /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i
  );

  return fileNameMatch?.[1]
    ? decodeURIComponent(fileNameMatch[1])
    : `email-templates.csv`;
};

const downloadCsv = (
  buffer: ArrayBuffer,
  fileName: string,
  contentType = 'text/csv'
) => {
  const blob = new Blob([buffer], { type: contentType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const defaultTemplatenameAndEvent = {
  name: '',
  event: '',
  tag: ''
};

export const getReminderEventsPhaseLabel = (
  eventData?: Partial<EventItem>,
  state?: string
): string => {
  if (!eventData?.event && !state) return '';
  const entityName = EventAndEntityNameMap[
    eventData?.event as keyof typeof EventAndEntityNameMap
  ] as keyof typeof reminderPhaseLabels;

  const phase = state as keyof (typeof reminderPhaseLabels)[typeof entityName];

  const labelConfig = reminderPhaseLabels[entityName]?.[phase];

  return labelConfig?.label ?? eventData?.eventDisplayName;
};

const TableHeader = () => (
  <Thead>
    <Tr
      data-testId="template-table-header"
      gap={2}
      sx={{
        '& th': {
          textTransform: 'none',
          color: 'black',
          background: 'white'
        }
      }}
    >
      {TEMPLATE_TABLE_COLUMNS.map(column => (
        <Th p={4} key={column.key} textAlign="left">
          <Text textStyle="headingItem" whiteSpace="nowrap">
            {column.label}
          </Text>
        </Th>
      ))}
      <Th p={4} key="status" width={20} textAlign="left">
        <Text textStyle="headingItem" whiteSpace="nowrap">
          Status
        </Text>
      </Th>
      <Th p={4} key="edit" width={20}></Th>
    </Tr>
  </Thead>
);

const EmailTemplatesTable = ({
  organizationId
}: {
  organizationId: string;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // State for managing UI page (1-based for display)
  const [uiPage, setUiPage] = useState<number>(1);
  // State for managing API page (0-based for backend)
  const [apiPage, setApiPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const toast = useToast();
  const queryClient = useQueryClient();
  // State for edit modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isOpenStatusModal, setIsOpenStatusModal] = useState(false);
  const [isOpenTemplatePreview, setIsOpenTemplatePreview] = useState(false);
  const [deleteTemplateState, setDeleteTemplateState] = useState<{
    templateId: string;
    isDefaultTemplate: boolean;
  } | null>(null);
  const [isStatus, setIsStatus] = useState<boolean | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null
  );
  const [searchValue, setSearchValue] = useState('');

  // State to check if template is being cloned
  const [isClone, setIsClone] = useState(false);

  // State for default template confirmation modal
  const [
    showDefaultTemplateConfirmationModal,
    setShowDefaultTemplateConfirmationModal
  ] = useState(false);

  // State to set the current selected email template to check default template
  const selectedTemplateData = useRef<EmailTemplateDoc | null>(null);

  // state to set template name and event name to change default template
  const templateNameAndEvent = useRef<{
    name: string;
    event: string;
    tag: string;
  }>(defaultTemplatenameAndEvent);

  // State for create modal
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const { watch, setValue, reset, getValues } = useForm<IListTemplatesQuery>({
    defaultValues
  });
  const filters = watch();

  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, string[]>
  >({});

  const { workspaceId } = useWorkspace();

  // Handle page change (convert from UI page to API page)
  const handlePageChange = (newPage: number) => {
    setUiPage(newPage);
    // API uses 0-based indexing and offset/skip instead of pages
    setApiPage((newPage - 1) * pageSize);
  };

  // Open edit modal for a template
  const handleEditTemplate = (templateId: string, isClone: boolean = false) => {
    setSelectedTemplateId(templateId);
    setIsEditModalOpen(true);
    setIsOpenTemplatePreview(false);
    setIsClone(isClone);
  };

  // Close edit modal
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setIsClone(false);
  };

  const handleOpenDeleteTemplateModal = (
    templateId: string,
    isDefaultTemplate: boolean = false
  ) => {
    setDeleteTemplateState({ templateId, isDefaultTemplate });
  };

  const handleCloseDeleteTemplateModal = () => {
    setDeleteTemplateState(null);
  };

  // Open create modal
  const handleOpenCreateModal = () => {
    setIsCreateModalOpen(true);
  };

  // Close create modal
  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleOpenStatusModal = (
    templateId: string,
    status: boolean,
    defaultTemplate: boolean
  ) => {
    if (defaultTemplate) {
      toast({
        variant: 'alert',
        title: 'Default template cannot be disabled',
        duration: 5000,
        isClosable: true
      });
      return;
    }

    setIsOpenStatusModal(prev => !prev);
    setIsStatus(status);
    setSelectedTemplateId(templateId);
  };

  const handleCloseStatusModal = () => {
    setIsOpenStatusModal(false);
    setSelectedTemplateId(null);
    setIsStatus(null);
  };

  const handleOpenTemplatePreviewModal = (templateId: string) => {
    setIsOpenTemplatePreview(prev => !prev);
    setSelectedTemplateId(templateId);
  };

  const handleCloseTemplatePreviewModal = () => {
    setIsOpenTemplatePreview(prev => !prev);
    setSelectedTemplateId(null);
  };
  const editTemplateMutation = useMutation({
    mutationFn: ({
      data,
      templateId
    }: {
      data: { active: boolean };
      templateId: string;
    }) => editEmailTemplateById(templateId, organizationId, data),
    onSuccess: (data, variables) => {
      toast({
        variant: 'success',
        title: `Template ${data.active ? 'enabled' : 'disabled'}`,
        description: `This email will ${
          data.active ? 'now' : 'no longer'
        }  be sent for this event`,
        duration: 3000,
        isClosable: true
      });

      queryClient.invalidateQueries({
        queryKey: [
          'email-template-details',
          variables.templateId,
          organizationId
        ]
      });
      queryClient.refetchQueries({
        queryKey: [
          'email-template-details',
          variables.templateId,
          organizationId
        ]
      });

      refetch();
      handleCloseStatusModal();
    },
    onError: error => {
      toast({
        variant: 'error',
        title: 'Failed to update template',
        description: String(error),
        duration: 5000,
        isClosable: true
      });
    }
  });

  const exportTemplatesMutation = useMutation({
    mutationFn: () => exportEmailTemplates(organizationId),
    onSuccess: response => {
      const contentType = String(
        response.headers?.['content-type'] ?? 'text/csv'
      );
      const fileName = getExportFileName(
        response.headers?.['content-disposition']
      );

      downloadCsv(response.data, fileName, contentType);
      toast({
        variant: 'success',
        title: 'Email templates exported',
        duration: 3000,
        isClosable: true
      });
    },
    onError: error => {
      toast({
        variant: 'error',
        title: 'Failed to export email templates',
        description: String(error),
        duration: 5000,
        isClosable: true
      });
    }
  });

  const handleStatusChanges = () => {
    if (isStatus && selectedTemplateId) {
      const data = {
        active: !isStatus
      };
      editTemplateMutation.mutate({ data, templateId: selectedTemplateId });
    }
  };
  const handleOpenStatus = (isStatus: boolean, templateId: string) => {
    const data = {
      active: !isStatus
    };
    editTemplateMutation.mutate({ data, templateId });
  };
  // Fetch events for dropdown in create modal
  const { data: eventsData } = useQuery({
    queryKey: ['email-events', { organizationId }],
    queryFn: () =>
      organizationId ? getEmailEventsWithSubEvents(organizationId) : null
  });

  // Fetch template data using React Query
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [
      'email-templates',
      { organizationId, skip: apiPage, filters, pageSize }
    ],
    queryFn: () =>
      organizationId
        ? getAllTemplates(organizationId, {
            skip: apiPage,
            limit: pageSize,
            ...filters
          })
        : null
  });

  // Total number of records across every page
  const totalItems = data?.total || 0;

  // If templates data is available, extract them
  const templates = data?.data || [];

  // Prepare events for the dropdown
  const events = eventsData?.events || [];
  const labels = eventsData?.labels || {};

  const hasFilters =
    !!filters?.search ||
    !!filters?.event?.length ||
    !!filters?.submissionContextId?.length ||
    !!filters?.category?.length ||
    !!filters?.scope?.length ||
    !!filters?.active?.length ||
    !!filters?.phases?.length;

  const offset = (uiPage - 1) * pageSize;

  const view = 'emails';
  const { data: dashBoardConfigs } = useQuery({
    queryKey: ['submission-dashboard-config', { view, workspaceId }],
    queryFn: () => fetchSubmissionsDashBoardConfig({ workspaceId, view })
  });

  const removeSingleFilter = (
    filterToRemove: string,
    fieldName: keyof IListTemplatesQuery
  ) => {
    const currentFilter = getValues(fieldName);

    // Always ensure it's an array of strings
    const currentArray: string[] = Array.isArray(currentFilter)
      ? currentFilter
      : currentFilter
      ? [String(currentFilter)]
      : [];

    const updatedFilter = currentArray.filter(f => f !== filterToRemove);

    setValue(fieldName, updatedFilter as any, {
      shouldValidate: true,
      shouldDirty: true
    });

    setSelectedFilters(prev => ({
      ...prev,
      [fieldName]: updatedFilter
    }));
  };

  const handleFiltersSubmit = (selectedFilters: Record<string, string[]>) => {
    Object.entries(selectedFilters).forEach(([key, values]) => {
      setValue(key as keyof IListTemplatesQuery, values as any);
    });
  };

  const clearFilters = () => {
    reset(defaultValues);
    setSearchValue('');
    setSelectedFilters({});

    setSearchParams({});
  };

  // Reset page when filter is applied
  const filtersKey = useMemo(
    () =>
      JSON.stringify({
        event: filters.event ?? [],
        submissionContextId: filters.submissionContextId ?? [],
        scope: filters.scope ?? [],
        active: filters.active ?? [],
        category: filters.category ?? [],
        phases: filters.phases ?? [],
        searchParams: (filters.search ?? '').trim()
      }),
    [
      filters.event,
      filters.submissionContextId,
      filters.scope,
      filters.active,
      filters.category,
      filters.phases,
      filters.search
    ]
  );
  useEffect(() => {
    setUiPage(1);
    setApiPage(0);
  }, [filtersKey]);

  useEffect(() => {
    const newFilters = parseEmailQueryParams(location.search);

    setSearchValue(newFilters.search || '');
    reset(newFilters);
    setSelectedFilters({
      event: newFilters.event ?? [],
      submissionContextId: newFilters.submissionContextId ?? [],
      scope: newFilters.scope ?? [],
      active: newFilters.active ?? [],
      category: newFilters.category ?? []
    });
  }, [reset, searchParams]);

  useEffect(() => {
    const query = searchParams.get('search');

    if (query && query !== searchValue) {
      setSearchValue(query);
    }
  }, [searchParams]);

  const startIndex = offset + 1;
  const endIndex = Math.min(offset + pageSize, totalItems);

  // Mutation method to validate default template
  const defaltTemplateValidation = useMutation({
    mutationFn: validateDefaultTemplate,
    onSuccess: data => {
      if (data.isValid) {
        editDefaultTemplateMutation.mutate({
          templateId: selectedTemplateData.current!._id,
          organizationId: selectedTemplateData.current!.organizationId,
          event: selectedTemplateData.current!.event,
          tag: selectedTemplateData.current!.tag!
        });
      } else {
        templateNameAndEvent.current = {
          name: data.name ?? '',
          event: selectedTemplateData.current?.event ?? '',
          tag: selectedTemplateData.current?.tag ?? ''
        };
        setShowDefaultTemplateConfirmationModal(true);
      }
    },
    onError: err => {
      toast({
        variant: 'error',
        title: 'Failed to update default template',
        description: String(err),
        duration: 5000,
        isClosable: true
      });
    }
  });

  // Calling Edit template API to make the template default
  const editDefaultTemplateMutation = useMutation({
    mutationFn: ({
      templateId,
      organizationId,
      event,
      tag,
      customizable
    }: {
      templateId: string;
      organizationId: string;
      event: string;
      tag?: string;
      customizable?: boolean;
    }) =>
      editEmailTemplateById(templateId, organizationId, {
        default: true,
        event,
        active: true,
        tag,
        customizable
      }),
    onSuccess: () => {
      toast({
        variant: 'success',
        title: 'Default template updated',
        duration: 3000,
        isClosable: true
      });

      if (templateNameAndEvent.current.name)
        setShowDefaultTemplateConfirmationModal(false);

      templateNameAndEvent.current = defaultTemplatenameAndEvent;

      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
    },
    onError: err => {
      toast({
        variant: 'error',
        title: 'Failed to edit default template',
        description: String(err),
        duration: 5000,
        isClosable: true
      });
    }
  });

  // ValidateDefaultTemplate
  const handleValidateDefaultTemplate = (
    organizationId: string,
    event: string,
    templateData: EmailTemplateDoc
  ) => {
    const data: IValidateDefaultTemplate = {
      organizationId,
      validateData: {
        event,
        default: true
      }
    };

    selectedTemplateData.current = templateData;

    defaltTemplateValidation.mutate(data);
  };

  const handleChangeDefaultTemplate = () => {
    editDefaultTemplateMutation.mutate({
      templateId: selectedTemplateData.current!._id,
      organizationId: selectedTemplateData.current!.organizationId,
      event: selectedTemplateData.current!.event,
      tag: selectedTemplateData.current!.tag,
      customizable: selectedTemplateData.current!.customizable
    });
  };

  return (
    <>
      {isOpenStatusModal && (
        <StatusActionModal
          onClose={handleCloseStatusModal}
          isOpenStatusModal={isOpenStatusModal}
          onStatusChange={handleStatusChanges}
        />
      )}
      {isOpenTemplatePreview && selectedTemplateId && (
        <TemplatePreview
          isOpen={isOpenTemplatePreview}
          organizationId={organizationId}
          templateId={selectedTemplateId}
          onClose={handleCloseTemplatePreviewModal}
          editModal={handleEditTemplate}
          events={events}
          labels={labels}
        />
      )}
      {/* Already exist default template modal */}
      {showDefaultTemplateConfirmationModal && (
        <DefaultEmailTemplateModal
          isOpen={showDefaultTemplateConfirmationModal}
          onClose={() => setShowDefaultTemplateConfirmationModal(false)}
          defaultEventDetail={templateNameAndEvent.current}
          onConfirmChange={handleChangeDefaultTemplate}
          loadingState={editDefaultTemplateMutation.isLoading}
        />
      )}
      <Flex direction="column" gap={4} width="100%" mt={2}>
        <Flex flexDirection={'column'} gap={4} width="100%">
          <Flex flexDirection={'column'} gap={2} mb={2}>
            <Flex gap={4} flexWrap="wrap">
              <Flex
                gap={2}
                alignItems="center"
                flex={{ base: '1 1 100%', md: '1' }}
              >
                <SearchInput
                  placeholder="Search emails by title"
                  iconLeft={<FontAwesomeIcon icon={faSearch} />}
                  width="100%"
                  name="searchParams"
                  value={searchValue}
                  enterKeyHint="search"
                  onChange={e => setSearchValue(e.currentTarget.value)}
                  onInputClear={() => {
                    setSearchValue('');
                    setValue('search', '');
                    if (searchParams.size) {
                      setSearchParams({});
                    }
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      setValue('search', searchValue.trim());
                    }
                  }}
                  onKeyUp={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      setValue('search', searchValue.trim());
                    }
                  }}
                />
                <Box display={{ base: 'block', lg: 'none' }} flexShrink={0}>
                  <Button
                    variant="primary"
                    aria-label="Search"
                    onClick={() => setValue('search', searchValue.trim())}
                  >
                    <FontAwesomeIcon icon={faSearch} />
                  </Button>
                </Box>
              </Flex>
              {dashBoardConfigs?.filters && (
                <FilterDrawer
                  filterGroups={dashBoardConfigs.filters.map((filter: any) => ({
                    ...filter,
                    items: filter.items ?? []
                  }))}
                  watch={watch}
                  onSubmit={handleFiltersSubmit}
                  selectedFilters={selectedFilters}
                  setSelectedFilters={setSelectedFilters}
                />
              )}
              <Flex>
                <Button
                  variant="secondary"
                  onClick={() => exportTemplatesMutation.mutate()}
                  leftIcon={<FontAwesomeIcon icon={faDownload} />}
                  isLoading={exportTemplatesMutation.isPending}
                >
                  Export Templates
                </Button>
              </Flex>
              <Flex>
                <Button
                  variant="primary"
                  onClick={handleOpenCreateModal}
                  leftIcon={<FontAwesomeIcon icon={faPlus} />}
                >
                  New Template
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </Flex>

        <Skeleton isLoaded={!isLoading}>
          <Flex alignItems={'center'} gap={2} minHeight={5} flexWrap="wrap">
            <Text textStyle="textBody">
              {totalItems > 0 ? (
                <>
                  Showing <strong>{startIndex}</strong> –{' '}
                  <strong>{endIndex}</strong> of <strong>{totalItems}</strong>{' '}
                  email template
                  {totalItems === 1 ? '' : 's'}
                </>
              ) : (
                <>
                  <strong>0</strong> email templates
                </>
              )}
            </Text>
            {hasFilters && (
              <ButtonText
                onClick={() => {
                  clearFilters();
                }}
                leftIcon={<FontAwesomeIcon icon={faDeleteRight} />}
                variant="secondary"
              >
                Clear filters
              </ButtonText>
            )}
          </Flex>
        </Skeleton>
        {!!hasFilters && (
          <Flex alignItems="center" gap={2} wrap="wrap">
            {dashBoardConfigs?.filters?.map(filterConfig => {
              // Type guard to check if filterConfig has 'items'
              const hasItems = (fc: any): fc is { items: any[] } =>
                Array.isArray(fc.items);

              if (!hasItems(filterConfig) || filterConfig.items.length === 0) {
                return null;
              }

              const filterKey = filterConfig.key as keyof typeof filters;
              const filterValues = filters?.[filterKey];

              if (!Array.isArray(filterValues) || filterValues.length === 0) {
                return null;
              }

              return filterValues.map(value => {
                const item = filterConfig.items.find(
                  (i: any) => i.value === value
                );
                const label = item?.name ?? value;

                return (
                  <Badge
                    key={`${filterKey}-${value}`}
                    size="base"
                    variant="interactive"
                  >
                    <Flex alignItems="center" gap={2}>
                      {label}
                      <FontAwesomeIcon
                        icon={faXmark}
                        cursor="pointer"
                        onClick={() =>
                          removeSingleFilter(value, filterKey as filtersItem)
                        }
                      />
                    </Flex>
                  </Badge>
                );
              });
            })}
          </Flex>
        )}
        {/* <Banner
          content={
            <Text>
              Please map email templates for the following events: Rejection,
              Author Revision, Decision, and Notification.
            </Text>
          }
          heading={`0 Events without mapped email templates`}
          variant="attention"
        /> */}
        {isLoading ? (
          <SubmissionLoader
            title="Loading Email Templates"
            subTitle="We are retrieving your email templates. This should only take a moment."
            bgColor="neutralSecondaryFill"
          />
        ) : isError ? (
          <SubmissionLoader
            title="Failed to Load Templates"
            subTitle="There was an error loading the email templates. Please try again later."
            bgColor="errorPrimaryFill"
          />
        ) : templates?.length > 0 ? (
          <>
            <Box overflowX="auto" width="100%">
              {/* @ts-ignore */}
              <Table size={'sm'} minWidth="700px">
                <TableHeader />
                <Tbody>
                  {templates.map(template => (
                    <Tr
                      key={template._id}
                      data-testId="email-template-table-row"
                      gap={2}
                      background="white"
                    >
                      {TEMPLATE_TABLE_COLUMNS.map(column =>
                        column.key === 'id' ? (
                          <Td key={column.key} textAlign="left">
                            <Text textStyle="textContext" whiteSpace="nowrap">
                              G: {template.templateId || 'N/A'}
                            </Text>
                            <Text textStyle="textContext" whiteSpace="nowrap">
                              J: {template.journalSeqId || 'N/A'}
                            </Text>
                          </Td>
                        ) : column.key === 'name' ? (
                          <Td
                            key={column.key}
                            cursor="pointer"
                            _hover={{ bg: 'gray.50' }}
                          >
                            <ClearButton
                              ariaLabel={`open ${column.key} review`}
                              width="100%"
                              onClick={() =>
                                handleOpenTemplatePreviewModal(template._id)
                              }
                            >
                              <Flex
                                direction="column"
                                alignItems="flex-start"
                                justifyContent="flex-start"
                                width="100%"
                                minWidth={0}
                                textAlign="left"
                              >
                                <Flex mb={1} gap={2}>
                                  <Badge size="context" variant="neutral">
                                    {template.customizable
                                      ? 'Configurable'
                                      : 'Not Configurable'}
                                  </Badge>
                                  {template.default && (
                                    <Badge size="context" variant="neutral">
                                      Default
                                    </Badge>
                                  )}
                                </Flex>
                                <Tooltip
                                  hasArrow
                                  borderRadius={4}
                                  placement="top"
                                  label={'View Email Template'}
                                >
                                  <Box width="100%">
                                    <Box noOfLines={1} isTruncated width="100%">
                                      <Text
                                        textStyle="headingItem"
                                        whiteSpace="normal"
                                        textDecoration="none"
                                        _hover={{
                                          textDecoration: 'underline',
                                          textDecorationColor:
                                            'actionPrimaryFill'
                                        }}
                                      >
                                        <RichTextPreview
                                          value={template.name}
                                          color="actionPrimaryFill"
                                        />
                                      </Text>
                                    </Box>
                                    <Text
                                      textStyle="textContext"
                                      whiteSpace="normal"
                                    >
                                      {template.editedBy?.name
                                        ? `Last edited by ${
                                            template.editedBy.name
                                          } on ${dateFormat(
                                            template.updatedAt,
                                            'mmm dd, yyyy'
                                          )}`
                                        : template.createdBy?.name
                                        ? `Last edited by ${
                                            template.createdBy.name
                                          } on ${dateFormat(
                                            template.updatedAt,
                                            'mmm dd, yyyy'
                                          )}`
                                        : ''}
                                    </Text>
                                  </Box>
                                </Tooltip>
                              </Flex>
                            </ClearButton>
                          </Td>
                        ) : column.key === 'event' ? (
                          <Td key={column.key} textAlign="left">
                            {template?.event
                              .toLowerCase()
                              .includes('reminder') ? (
                              <Tooltip
                                hasArrow
                                shouldWrapChildren
                                placement="top"
                                label={getReminderEventsPhaseLabel(
                                  events.find(e => e.event === template.event),
                                  template.state!
                                )}
                                borderRadius={4}
                              >
                                {
                                  events.find(e => e.event === template.event)
                                    ?.eventDisplayName
                                }
                              </Tooltip>
                            ) : (
                              <>{labels[template.event]?.label}</>
                            )}
                          </Td>
                        ) : column.key === 'scope' ? (
                          <Td key={column.key} textAlign="left">
                            {template.scope === 'global' ? (
                              'Global/Workspace'
                            ) : (
                              <>
                                <Text mb={1}>Journal Specific</Text>
                                {template.submissionContextId && (
                                  <Badge variant="neutral">
                                    <Text fontWeight="bold">
                                      {template.submissionContextId.length < 2
                                        ? `${template.submissionContextId.length} Journal`
                                        : `+${template.submissionContextId.length} Journals`}
                                    </Text>
                                  </Badge>
                                )}
                              </>
                            )}
                          </Td>
                        ) : (
                          <Td
                            key={column.key}
                            textAlign="left"
                            textTransform="capitalize"
                          >
                            {(() => {
                              const value =
                                template[column.key as keyof EmailTemplateDoc];

                              if (value) return <>{String(value)}</>;
                              return 'N/A';
                            })()}
                          </Td>
                        )
                      )}
                      <Td key="actions" width={20} textAlign="left">
                        <Switch
                          isChecked={template.active}
                          onChange={() =>
                            template.active
                              ? handleOpenStatusModal(
                                  template._id,
                                  template.active,
                                  template.default!
                                )
                              : handleOpenStatus(
                                  !!template.active,
                                  template._id
                                )
                          }
                        />
                      </Td>
                      <Td key="actions" width={20} textAlign="right">
                        <EmailActionMenu
                          templateId={template._id}
                          isConfigurable={template.customizable}
                          isDefault={template.default}
                          onValidateDefaultTemplate={() =>
                            handleValidateDefaultTemplate(
                              template.organizationId,
                              template.event,
                              template
                            )
                          }
                          handleClick={(_, isCloneEmailTemplate) =>
                            handleEditTemplate(
                              template._id,
                              isCloneEmailTemplate
                            )
                          }
                          handleDeleteClick={() =>
                            handleOpenDeleteTemplateModal(
                              template._id,
                              !!template.default
                            )
                          }
                        />
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>

            <TablePagination
              ariaLabel="Email templates pagination"
              currentPage={uiPage}
              totalItems={totalItems}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={setPageSize}
            />
          </>
        ) : (
          <SubmissionLoader
            title="No Email Templates Found"
            subTitle="There are no email templates configured for this organization."
            icon={faEnvelope}
            bgColor="neutralSecondaryFill"
          />
        )}

        {/* Edit Template Modal */}
        {isEditModalOpen && selectedTemplateId && (
          <EditTemplateModal
            isOpen={isEditModalOpen}
            onClose={handleCloseEditModal}
            organizationId={organizationId}
            templateId={selectedTemplateId}
            onSuccess={refetch}
            isEmailTemplateClone={isClone}
            setIsClone={setIsClone}
            isCreating={false}
            categories={[]}
          />
        )}

        {deleteTemplateState && (
          <DeleteTemplateModal
            templateId={deleteTemplateState.templateId}
            organizationId={organizationId}
            isDefaultTemplate={deleteTemplateState.isDefaultTemplate}
            onClose={handleCloseDeleteTemplateModal}
            onSuccess={refetch}
          />
        )}

        {/* Create Template Modal */}
        {isCreateModalOpen && (
          <EditTemplateModal
            isOpen={isCreateModalOpen}
            onClose={handleCloseCreateModal}
            organizationId={organizationId}
            templateId=""
            onSuccess={refetch}
            isCreating={true}
            categories={[]}
          />
        )}
      </Flex>
    </>
  );
};

export default EmailTemplatesTable;
